import { USERNAME_POOL } from "../data/usernamePool";

// ─── Constants ────────────────────────────────────────────────────────────

// Currency split — Gold Coin play is the dominant mode; Cluck Buck wins
// are rarer and feel more "real money".
const GOLD_PROBABILITY = 0.7;

// Win/loss split. We bias slightly toward wins because social proof skews
// to celebrations, but we keep enough losses to feel honest.
const WIN_PROBABILITY = 0.65;

// Within wins, what fraction are BIG WINs (subject to no-adjacency rule).
const BIG_WIN_PROBABILITY = 0.22;

// Cap for the live feed buffer. Sized so the largest breakpoint shows
// (FEED_MAX_LENGTH - 1) cards with one buffered/cropped — that buffer is
// what slides into view when the oldest drops off as a new card arrives.
export const FEED_MAX_LENGTH = 4;

// Offline fallback game pool — real Hub88 titles with the default
// placeholder thumbnail. The placeholder is fine here per design direction;
// when the backend is up, real `local_image_url` values are used instead.
const FALLBACK_THUMBNAIL = "/assets/games/default-game.png";

const FALLBACK_GAMES = [
  {
    name: "Sweet Bonanza",
    game_code: "sweet_bonanza",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  {
    name: "Gates of Olympus",
    game_code: "gates_of_olympus",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  {
    name: "Big Bass Bonanza",
    game_code: "big_bass_bonanza",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  {
    name: "Sugar Rush",
    game_code: "sugar_rush",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  { name: "Wolf Gold", game_code: "wolf_gold", thumbnail: FALLBACK_THUMBNAIL },
  {
    name: "The Dog House",
    game_code: "the_dog_house",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  {
    name: "Wanted Dead or a Wild",
    game_code: "wanted_dead_or_a_wild",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  {
    name: "Money Train 4",
    game_code: "money_train_4",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  {
    name: "Book of Dead",
    game_code: "book_of_dead",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  { name: "Starburst", game_code: "starburst", thumbnail: FALLBACK_THUMBNAIL },
  {
    name: "Mega Moolah",
    game_code: "mega_moolah",
    thumbnail: FALLBACK_THUMBNAIL,
  },
  { name: "Plinko", game_code: "plinko", thumbnail: FALLBACK_THUMBNAIL },
];

// ─── Pure helpers ─────────────────────────────────────────────────────────

const randInRange = (min, max) => min + Math.random() * (max - min);

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

let activityCounter = 0;
const nextActivityId = () => `activity-${Date.now()}-${++activityCounter}`;

// Outcome amounts. Distributions are weighted to favour smaller amounts
// inside each band so the feed feels realistic — most wins are modest,
// jackpots are rare.

const pickGoldBigWin = () => {
  const r = Math.random();
  if (r < 0.65) return randInRange(1000, 5000); // most BIG WINs are 1k–5k
  if (r < 0.9) return randInRange(5000, 10000); // some 5k–10k
  if (r < 0.98) return randInRange(10000, 15000); // few 10k–15k
  return randInRange(15000, 20000); // rare 15k–20k
};

const pickGoldWin = () => {
  const r = Math.random();
  if (r < 0.5) return randInRange(50, 250); // small wins dominate
  if (r < 0.85) return randInRange(250, 600);
  return randInRange(600, 999);
};

const pickGoldLoss = () => {
  const r = Math.random();
  if (r < 0.65) return randInRange(25, 100);
  if (r < 0.95) return randInRange(100, 300);
  return randInRange(300, 500);
};

const pickCluckBigWin = () => {
  const r = Math.random();
  if (r < 0.7) return randInRange(100, 250); // most BIG WINs $100–$250
  if (r < 0.95) return randInRange(250, 400); // some $250–$400
  return randInRange(400, 500); // rare $400–$500
};

const pickCluckWin = () => {
  const r = Math.random();
  if (r < 0.6) return randInRange(1, 25);
  if (r < 0.9) return randInRange(25, 50);
  return randInRange(50, 99);
};

const pickCluckLoss = () => {
  const r = Math.random();
  if (r < 0.7) return randInRange(0.5, 10);
  if (r < 0.95) return randInRange(10, 25);
  return randInRange(25, 30);
};

const pickAmount = (currency, outcome) => {
  if (currency === "gold") {
    if (outcome === "bigWin") return pickGoldBigWin();
    if (outcome === "win") return pickGoldWin();
    return pickGoldLoss();
  }
  if (outcome === "bigWin") return pickCluckBigWin();
  if (outcome === "win") return pickCluckWin();
  return pickCluckLoss();
};

const pickCurrency = () =>
  Math.random() < GOLD_PROBABILITY ? "gold" : "cluck";

const pickPlayer = (lastUsername) => {
  // Pull from the static pool; reroll if we'd produce two of the same
  // username in a row. With a 100-name pool the second pick almost never
  // collides, so the loop terminates immediately in practice.
  let pick = pickRandom(USERNAME_POOL);
  while (pick === lastUsername) pick = pickRandom(USERNAME_POOL);
  return pick;
};

const pickGame = (games, lastGameCode) => {
  // Same idea: avoid showing the same game in two adjacent cards.
  let pick = pickRandom(games);
  let attempts = 0;
  while (pick.game_code === lastGameCode && attempts < 5) {
    pick = pickRandom(games);
    attempts++;
  }
  return pick;
};

// ─── Outcome sequencing ───────────────────────────────────────────────────

// Pure roll for a SINGLE new live activity. Avoids loss streaks and
// adjacent BIG WINs by reading the previous activity's outcome.
const rollOutcome = (lastOutcome) => {
  // No two losses in a row.
  const wantsLoss = lastOutcome !== "loss" && Math.random() > WIN_PROBABILITY;
  if (wantsLoss) return "loss";

  // No two BIG WINs in a row.
  const couldBeBigWin = lastOutcome !== "bigWin";
  const isBigWin = couldBeBigWin && Math.random() < BIG_WIN_PROBABILITY;
  return isBigWin ? "bigWin" : "win";
};

// Build a sequence of outcomes for the initial seed. Guarantees one BIG WIN
// somewhere in the first 3 slots (the "look how alive this site is" effect
// the user sees when the page first loads).
const buildOutcomeSequence = (count) => {
  const outcomes = [];
  for (let i = 0; i < count; i++) {
    outcomes.push(rollOutcome(outcomes[i - 1]));
  }

  // Guarantee a BIG WIN in slots 0-2.
  const firstThree = outcomes.slice(0, Math.min(3, count));
  if (!firstThree.includes("bigWin")) {
    for (let i = 0; i < Math.min(3, count); i++) {
      const prev = outcomes[i - 1];
      const next = outcomes[i + 1];
      if (prev !== "bigWin" && next !== "bigWin") {
        outcomes[i] = "bigWin";
        break;
      }
    }
  }

  return outcomes;
};

// ─── Timestamp windows ────────────────────────────────────────────────────

// Each slot's age window in milliseconds — strictly ascending so the feed
// reads "most recent at the front."
const SEED_TIMESTAMP_WINDOWS_MS = [
  [5_000, 30_000], // slot 0: 5–30s
  [30_000, 60_000], // slot 1: 30–60s
  [60_000, 120_000], // slot 2: 1–2m
  [120_000, 240_000], // slot 3: 2–4m
  [240_000, 420_000], // slot 4: 4–7m
];

const pickSeedTimestamp = (slotIndex) => {
  const window =
    SEED_TIMESTAMP_WINDOWS_MS[slotIndex] ||
    SEED_TIMESTAMP_WINDOWS_MS[SEED_TIMESTAMP_WINDOWS_MS.length - 1];
  const [minMs, maxMs] = window;
  return new Date(Date.now() - randInRange(minMs, maxMs));
};

// ─── Public API ───────────────────────────────────────────────────────────

export const getFallbackGames = () => FALLBACK_GAMES;

/**
 * Normalise a "real games" array (response from casinoService.getNewGames)
 * into the shape this generator works with.
 */
export const normalizeRealGames = (games) => {
  if (!Array.isArray(games) || games.length === 0) return null;
  return games
    .filter((g) => g && g.game_code && g.name)
    .map((g) => ({
      name: g.name,
      game_code: g.game_code,
      thumbnail:
        g.local_image_url ||
        g.thumbnail_url ||
        g.url_thumb ||
        g.thumbnail ||
        FALLBACK_THUMBNAIL,
    }));
};

/**
 * Build one activity record. Used by both the seed loop and the live ticker.
 */
const buildActivity = ({ outcome, games, prevActivity, timestamp }) => {
  const currency = pickCurrency();
  const amount = pickAmount(currency, outcome);
  const username = pickPlayer(prevActivity?.user?.username);
  const game = pickGame(games, prevActivity?.game?.game_code);

  return {
    id: nextActivityId(),
    game: {
      name: game.name,
      game_code: game.game_code,
      thumbnail: game.thumbnail,
    },
    user: { username },
    amount,
    currency,
    isWin: outcome !== "loss",
    // BIG WIN gets the gold treatment via the threshold logic in
    // SocialProofCard. We pass the amount only; the card determines BIG
    // WIN status itself based on `amount` ≥ the configured threshold.
    timestamp,
  };
};

/**
 * Generate the initial seed of `count` activities for the page-load feed.
 * Outcomes follow the "no adjacent BIG WIN / no loss streak / one BIG WIN
 * in first 3" rules. Timestamps walk from seconds to minutes, ascending.
 */
export const generateInitialFeed = (rawGames, count = FEED_MAX_LENGTH) => {
  const games = normalizeRealGames(rawGames) || FALLBACK_GAMES;
  const outcomes = buildOutcomeSequence(count);

  const activities = [];
  for (let i = 0; i < count; i++) {
    const prev = activities[i - 1];
    activities.push(
      buildActivity({
        outcome: outcomes[i],
        games,
        prevActivity: prev,
        timestamp: pickSeedTimestamp(i),
      }),
    );
  }
  return activities;
};

/**
 * Generate ONE new "just now" activity for the live ticker. Reads the
 * current head of the feed to enforce no-adjacency rules.
 */
export const generateLiveActivity = (rawGames, currentFeed = []) => {
  const games = normalizeRealGames(rawGames) || FALLBACK_GAMES;
  const head = currentFeed[0];
  const lastOutcome = !head
    ? null
    : !head.isWin
      ? "loss"
      : isBigWinAmount(head)
        ? "bigWin"
        : "win";

  return buildActivity({
    outcome: rollOutcome(lastOutcome),
    games,
    prevActivity: head,
    timestamp: new Date(),
  });
};

// Mirror of the threshold defined in SocialProofCard so the generator can
// reason about BIG WIN adjacency without importing the component.
const BIG_WIN_THRESHOLD = { gold: 1000, cluck: 100 };
const isBigWinAmount = (activity) => {
  if (!activity || !activity.isWin) return false;
  const threshold = BIG_WIN_THRESHOLD[activity.currency];
  return (
    typeof threshold === "number" && Math.abs(activity.amount) >= threshold
  );
};
